import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter, Search } from 'lucide-react';
import { useGatePasses } from '../../hooks/useGatePasses.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { locationHooks } from '../../hooks/useMasters.js';
import { canCreateGatePass } from '../../utils/permissions.js';
import StatusBadge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';
import { GP_TYPE_LABELS } from '../../utils/constants.js';
import { formatDate } from '../../utils/formatters.js';

export default function GatePassListPage() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ page: 1, limit: 20, search: '', type: '', status: '', locationId: '' });
  const { data, isLoading } = useGatePasses(filters);
  const { data: locations = [] } = locationHooks.useList();

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gate Passes</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.total ?? '—'} total records</p>
        </div>
        {canCreateGatePass(user) && (
          <Link to="/gate-passes/new"><Button icon={Plus}>New Gate Pass</Button></Link>
        )}
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Search GP number, vehicle, driver…"
              value={filters.search} onChange={e => set('search', e.target.value)}
            />
          </div>
          <select value={filters.type} onChange={e => set('type', e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">All types</option>
            {Object.entries(GP_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={filters.status} onChange={e => set('status', e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">All statuses</option>
            {['DRAFT','SUBMITTED','CHECKED','APPROVED','DISPATCHED','RECEIVED','DELIVERED','RETURNED','ACCEPTED','REJECTED','CANCELLED','ARRIVING'].map(s => (
              <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
            ))}
          </select>
          <select value={filters.locationId} onChange={e => set('locationId', e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">All locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </select>
        </div>
      </Card>

      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['GP Number','Type','Location','Owner','Vehicle','Status','Date',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.items || []).map(gp => (
                  <tr key={gp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/gate-passes/${gp.id}`} className="font-mono font-semibold text-teal-700 hover:underline">
                        {gp.number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{GP_TYPE_LABELS[gp.type]}</td>
                    <td className="px-4 py-3 text-gray-600">{gp.sourceLocation?.code}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {gp.ownerType === 'MERCHANT_EXPORTER' ? gp.merchantExporter?.name : 'Sreeragam'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{gp.vehicleNumber || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={gp.status} /></td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(gp.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/gate-passes/${gp.id}`} className="text-xs text-teal-600 hover:underline">View →</Link>
                    </td>
                  </tr>
                ))}
                {data?.items?.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No gate passes found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {data && data.total > data.limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between text-sm text-gray-500">
            <span>Page {data.page} · {data.total} total</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={data.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Prev</Button>
              <Button variant="secondary" size="sm" disabled={data.page * data.limit >= data.total}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
